import isEmpty from 'lodash/isEmpty';
import { UserPermissions } from '@creditsights/api-ui-web';
import { hasCompanyRiskMetrics } from '../../../components/widgets/RiskProducts/RiskProductsWidget.logic';
import { FundamentalsMetrics, RiskMetrics, RiskOverviewMetrics } from '../../../models/RiskProducts';
import { CompanyWidgetId, RISK_TAB_WIDGET_ID } from '../Company.constants';
import { hasPermissionToRp, hasUIPermission } from '../../../utils/permissionsUtils';

interface RiskTabWidgetConfig {
  widgetId: RISK_TAB_WIDGET_ID;
  isAccessible?: () => boolean;
  hasData: () => boolean;
  isError: () => boolean;
}

const getRiskWidgetsConfiguration = (
  riskMetrics: RiskMetrics,
  riskOverviewMetrics: RiskOverviewMetrics | undefined,
  fundamentalsMetrics: FundamentalsMetrics | undefined,
  errorStates: {
    isRiskOverviewMetricsError: boolean;
    isRiskMetricsError: boolean;
    isFundamentalsError: boolean;
  },
  userPermissions?: UserPermissions
): RiskTabWidgetConfig[] => [
  {
    widgetId: RISK_TAB_WIDGET_ID.RISK_OVERVIEW,
    hasData: () => !isEmpty(riskOverviewMetrics),
    isError: () => errorStates.isRiskOverviewMetricsError,
  },
  {
    widgetId: RISK_TAB_WIDGET_ID.CQS_SUMMARY,
    hasData: () => true,
    isError: () => false,
  },
  {
    widgetId: RISK_TAB_WIDGET_ID.CRE_SUMMARY,
    hasData: () => true,
    isError: () => false,
  },
  {
    widgetId: RISK_TAB_WIDGET_ID.RISK_PRODUCT,
    hasData: () => !!riskMetrics && hasCompanyRiskMetrics(riskMetrics.cqsMetrics, riskMetrics.creMetrics),
    isError: () => errorStates.isRiskMetricsError,
  },
  {
    widgetId: RISK_TAB_WIDGET_ID.FUNDAMENTALS,
    hasData: () => !!fundamentalsMetrics,
    isError: () => errorStates.isFundamentalsError,
  },
  {
    widgetId: RISK_TAB_WIDGET_ID.COMPANY_OVERVIEW,
    hasData: () => true,
    isError: () => false,
    isAccessible: () => hasPermissionToRp(userPermissions) && !hasUIPermission(userPermissions),
  },
];

export const getFilteredWidgets = (
  widgets: CompanyWidgetId[],
  riskMetrics: RiskMetrics,
  riskOverviewMetrics: RiskOverviewMetrics | undefined,
  fundamentalsMetrics: FundamentalsMetrics | undefined,
  errorStates: {
    isRiskOverviewMetricsError: boolean;
    isRiskMetricsError: boolean;
    isFundamentalsError: boolean;
  }
): CompanyWidgetId[] => {
  const widgetsConfiguration = getRiskWidgetsConfiguration(
    riskMetrics,
    riskOverviewMetrics,
    fundamentalsMetrics,
    errorStates
  );
  return widgets.filter((widget) => {
    const widgetConfig = widgetsConfiguration.find((widgetConfig) => widgetConfig.widgetId === widget);
    if (widgetConfig?.isAccessible && !widgetConfig.isAccessible()) {
      return false;
    }
    return widgetConfig?.hasData() || widgetConfig?.isError();
  });
};
